/**
 * Admin Sidebar Navigation
 * Reorganized for better UX with combined tabs
 */
import React from 'react';
import { 
  LayoutDashboard, Users, Package, Globe, Megaphone, Calendar,
  Settings, Shield, Flag, FileText, Activity,
  ChevronDown, ChevronUp, LineChart, Layout, Languages,
  BadgeCheck, MessageSquare, Menu, X, Bell, Rocket, Gift, Radio, MapPin
} from 'lucide-react';

const AdminSidebar = ({ 
  activeTab, 
  setActiveTab, 
  subTab,
  setSubTab,
  pendingCounts = {},
  mobileMenuOpen,
  setMobileMenuOpen,
  isMobileHeader = false
}) => {
  // Reorganized tabs for better UX
  const tabs = [
    { 
      id: 'dashboard', 
      label: 'Dashboard', 
      icon: LayoutDashboard,
      description: 'Overview & analytics',
      subTabs: [
        { id: 'overview', label: 'Overview', icon: LayoutDashboard },
        { id: 'boost', label: 'Boost Analytics', icon: Rocket },
      ]
    },
    { 
      id: 'users', 
      label: 'Users', 
      icon: Users,
      badge: pendingCounts.businessDocs || 0,
      subTabs: [
        { id: 'management', label: 'User Management', icon: Users },
        { id: 'business-verification', label: 'Business Verification', icon: BadgeCheck, badge: pendingCounts.businessDocs },
      ]
    },
    { 
      id: 'content', 
      label: 'Content', 
      icon: Package,
      subTabs: [
        { id: 'categories', label: 'Categories', icon: Package },
        { id: 'translations', label: 'Translations', icon: Languages },
        { id: 'listings', label: 'Listings', icon: Package },
        { id: 'listing-maintenance', label: 'Listing Onderhoud', icon: Activity },
      ]
    },
    { 
      id: 'islands', 
      label: 'Islands', 
      icon: Globe,
      description: 'Manage marketplace islands'
    },
    { 
      id: 'advertising', 
      label: 'Advertising', 
      icon: Megaphone,
      description: 'Manage banners & placements'
    },
    { 
      id: 'ticker', 
      label: 'News Ticker', 
      icon: Radio,
      description: 'Scrolling announcements'
    },
    { 
      id: 'hotspots', 
      label: 'Featured Partners', 
      icon: MapPin,
      description: 'Manage hotspot partners'
    },
    { 
      id: 'referrals', 
      label: 'Share & Win', 
      icon: Gift,
      description: 'Referral campaign management'
    },
    { 
      id: 'events', 
      label: 'Events', 
      icon: Calendar,
      badge: pendingCounts.events,
      description: 'Manage marketplace events'
    },
    { 
      id: 'moderation', 
      label: 'Moderation', 
      icon: Shield,
      badge: (pendingCounts.reports || 0) + (pendingCounts.inquiries || 0),
      subTabs: [
        { id: 'reports', label: 'Reports', icon: Flag, badge: pendingCounts.reports },
        { id: 'inquiries', label: 'Inquiries', icon: MessageSquare, badge: pendingCounts.inquiries },
      ]
    },
    { 
      id: 'system', 
      label: 'System', 
      icon: Settings,
      subTabs: [
        { id: 'announcements', label: 'Announcements', icon: Bell },
        { id: 'settings', label: 'Settings', icon: Settings },
        { id: 'logs', label: 'Activity Logs', icon: FileText },
        { id: 'health', label: 'System Health', icon: Activity },
      ]
    },
  ];
  
  const handleTabClick = (tabId, hasSubTabs = false) => {
    setActiveTab(tabId);
    // Set default sub-tab when clicking parent
    if (hasSubTabs) {
      const tab = tabs.find(t => t.id === tabId);
      if (tab?.subTabs?.length > 0) {
        setSubTab(tab.subTabs[0].id);
      }
    } else {
      setSubTab(null);
    }
    if (setMobileMenuOpen) setMobileMenuOpen(false);
  };
  
  const handleSubTabClick = (parentId, subTabId) => {
    setActiveTab(parentId);
    setSubTab(subTabId);
    if (setMobileMenuOpen) setMobileMenuOpen(false);
  };
  
  const renderTabButton = (tab) => {
    const Icon = tab.icon;
    const isActive = activeTab === tab.id;
    const hasSubTabs = tab.subTabs && tab.subTabs.length > 0;
    
    const baseClass = `w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
      isActive 
        ? 'bg-primary text-primary-foreground font-medium shadow-sm' 
        : 'text-muted-foreground hover:text-foreground hover:bg-secondary/70'
    }`;
    
    if (hasSubTabs) {
      return (
        <div key={tab.id} className="space-y-1.5">
          <button
            onClick={() => handleTabClick(tab.id, true)}
            className={`${baseClass} justify-between`}
          >
            <span className="flex items-center gap-3">
              <Icon className="w-4 h-4" />
              {tab.label}
              {tab.badge > 0 && (
                <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center font-medium">
                  {tab.badge}
                </span>
              )}
            </span>
            {isActive ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          {isActive && (
            <div className="ml-4 pl-3 border-l-2 border-primary/30 space-y-1 py-1">
              {tab.subTabs.map(sub => {
                const SubIcon = sub.icon;
                const isSubActive = subTab === sub.id;
                return (
                  <button
                    key={sub.id}
                    onClick={() => handleSubTabClick(tab.id, sub.id)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isSubActive 
                        ? 'bg-primary/15 text-primary font-medium' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <SubIcon className="w-4 h-4" />
                      {sub.label}
                    </span>
                    {sub.badge > 0 && (
                      <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[18px] text-center font-medium">
                        {sub.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    
    return (
      <button
        key={tab.id}
        onClick={() => handleTabClick(tab.id)}
        className={baseClass}
      >
        <Icon className="w-4 h-4" />
        {tab.label}
        {tab.badge > 0 && (
          <span className="ml-auto bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center font-medium">
            {tab.badge}
          </span>
        )}
      </button>
    );
  };
  
  // Section headers for grouping
  const renderSection = (title, tabIds) => {
    const sectionTabs = tabs.filter(t => tabIds.includes(t.id));
    if (sectionTabs.length === 0) return null;
    
    return (
      <div className="space-y-2">
        <div className="px-3 py-1.5 text-[11px] font-semibold text-muted-foreground/70 uppercase tracking-wider">
          {title}
        </div>
        <div className="space-y-1">
          {sectionTabs.map(tab => renderTabButton(tab))}
        </div>
      </div>
    );
  };
  
  // Get current tab info for mobile header
  const currentTab = tabs.find(t => t.id === activeTab);
  const CurrentIcon = currentTab?.icon || Shield;
  
  // Mobile Header Only
  if (isMobileHeader) {
    return (
      <>
        {/* Mobile Admin Header */}
        <div className="bg-card/95 backdrop-blur-sm border-b border-border">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <CurrentIcon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="font-semibold text-sm">{currentTab?.label || 'Admin'}</h1>
                <p className="text-xs text-muted-foreground">Admin Panel</p>
              </div>
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`p-2.5 rounded-xl transition-colors ${
                mobileMenuOpen 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-secondary hover:bg-secondary/80'
              }`}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Sidebar Drawer */}
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
              onClick={() => setMobileMenuOpen(false)}
            />
            {/* Drawer */}
            <div className="fixed top-0 right-0 z-50 w-72 h-full bg-card border-l border-border shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  <span className="font-semibold">Admin Menu</span>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-lg hover:bg-secondary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 space-y-5">
                {renderSection('Overview', ['dashboard'])}
                {renderSection('Management', ['users', 'content', 'islands'])}
                {renderSection('Commerce', ['advertising', 'ticker', 'hotspots', 'referrals', 'events'])}
                {renderSection('Admin', ['moderation', 'system'])}
              </div>
            </div>
          </>
        )}
      </>
    );
  }
  
  // Desktop Sidebar Only
  return (
    <aside className="flex flex-col w-64 bg-card border-r border-border overflow-y-auto h-[calc(100vh-64px)] sticky top-16">
      <div className="p-5 border-b border-border">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          Admin Panel
        </h2>
        <p className="text-xs text-muted-foreground mt-1">Manage your marketplace</p>
      </div>
      
      <div className="flex-1 p-4 space-y-5 overflow-y-auto">
        {renderSection('Overview', ['dashboard'])}
        {renderSection('Management', ['users', 'content', 'islands'])}
        {renderSection('Commerce', ['advertising', 'ticker', 'hotspots', 'referrals', 'events'])}
        {renderSection('Admin', ['moderation', 'system'])}
      </div>
      
      {/* Quick Stats Footer */}
      <div className="p-4 border-t border-border bg-secondary/30">
        <div className="flex items-center justify-around text-xs">
          <div className="text-center">
            <div className="font-bold text-lg text-primary">{pendingCounts.verification || 0}</div>
            <div className="text-muted-foreground">Pending</div>
          </div>
          <div className="text-center">
            <div className="font-bold text-lg text-amber-500">{pendingCounts.reports || 0}</div>
            <div className="text-muted-foreground">Reports</div>
          </div>
          <div className="text-center">
            <div className="font-bold text-lg text-blue-500">{pendingCounts.inquiries || 0}</div>
            <div className="text-muted-foreground">Inquiries</div>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default AdminSidebar;
